import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import Header from './components/Header';
import Terminal from './components/Terminal';
import TeamMembers from './components/TeamMembers';
import Footer from './components/Footer';
import StickyVerticalMenu from './components/StickyVerticalMenu';
import MobileNavigationMenu from './components/MobileNavigationMenu';

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [bootSequence, setBootSequence] = useState(0);
  const [isBooted, setIsBooted] = useState(false);
  const [glitchText, setGlitchText] = useState('PaidNerd');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Memoize static arrays to prevent unnecessary re-renders
  const bootMessages = useMemo(() => [
    'Initializing PaidNerd Systems...',
    'Loading Web Development Modules...',
    'Establishing Secure Connection...',
    'Mounting Creative Filesystem...',
    'Starting Web Development Services...',
    'Loading Website Components...',
    'PaidNerd Terminal Ready ✓'
  ], []);

  const glitchChars = useMemo(() => '!<>-_\\/[]{}—=+*^?#________', []);

  useEffect(() => {
    // Boot sequence - optimized with early return
    const bootInterval = setInterval(() => {
      setBootSequence(prev => {
        if (prev >= bootMessages.length - 1) {
          clearInterval(bootInterval);
          setIsBooted(true);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(bootInterval);
  }, [bootMessages.length]);

  // Optimized glitch effect with useCallback
  const glitchTextHandler = useCallback(() => {
    const companyName = 'PaidNerd';
    if (Math.random() < 0.1) { // 10% chance to glitch
      const glitched = companyName.split('').map((char: string) => 
        Math.random() < 0.3 ? glitchChars[Math.floor(Math.random() * glitchChars.length)] : char
      ).join('');
      setGlitchText(glitched);
      
      setTimeout(() => setGlitchText(companyName), 100);
    }
  }, [glitchChars]);

  useEffect(() => {
    const glitchInterval = setInterval(glitchTextHandler, 2000);
    return () => clearInterval(glitchInterval);
  }, [glitchTextHandler]);

  // Optimized Matrix rain effect with improved performance
  useEffect(() => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pre-calculate font metrics for better performance
    ctx.font = '15px monospace';
    const charWidth = 20;
    const charHeight = 20;
    
    let drops: number[] = [];
    let animationId: number;
    let lastTime = 0;
    const fps = 30; // Limit FPS for better performance
    const fpsInterval = 1000 / fps;

    // Set canvas size and initialize drops
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Calculate columns based on actual canvas width
      const columns = Math.floor(canvas.width / charWidth);
      drops = new Array(columns).fill(1);
    };
    resizeCanvas();

    const draw = () => {
      // More efficient background fade
      ctx.fillStyle = 'rgba(13, 17, 23, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#56d4dd'; // terminal-cyan color
      ctx.font = '15px monospace';

      // Draw characters
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        const x = i * charWidth;
        const y = drops[i] * charHeight;

        ctx.fillText(text, x, y);

        // Reset drop randomly for continuous effect
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    // Optimized animation loop with FPS limiting
    const animate = (currentTime: number) => {
      if (currentTime - lastTime >= fpsInterval) {
        draw();
        lastTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);

    // Handle resize
    const handleResize = () => {
      resizeCanvas();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className={`app ${isLoaded ? 'fade-in' : ''}`}>
      <canvas ref={canvasRef} className="matrix-rain" />
      
      {!isBooted && (
        <div className="boot-sequence">
          <div className="boot-logo">
            <div className="hologram-effect">
              <img 
                src="/PaidNerd-Logo-with-text-colored.svg" 
                alt="PaidNerd Logo" 
                className="logo boot-logo-img"
              />
            </div>
          </div>
          <div className="boot-messages">
            {bootMessages.slice(0, bootSequence + 1).map((message, index) => (
              <div key={index} className={`boot-message ${index === bootSequence ? 'current' : ''}`}>
                <span className="boot-prompt">[SYSTEM]</span> {message}
              </div>
            ))}
          </div>
        </div>
      )}

      {isBooted && (
        <>
          <StickyVerticalMenu />
          <MobileNavigationMenu />
          <div className="hero-section">
            <Header />
          </div>
          <div className="terminal-section">
            <Terminal />
          </div>
          <div className="team-members-section">
            <TeamMembers />
          </div>
          <Footer />
        </>
      )}
    </div>
  );
}

export default App;
